import { type IReactionDisposer, makeAutoObservable, reaction, runInAction } from 'mobx'
import { bannerStore, EnumAlertBannerVariant } from 'shared/ui'
import { uiStore } from 'shared/store/uiStore'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { IntegrationsPath } from 'pages/settings/pages/integrations/route/integrationsPath'

export class IntegrationBannerStore {
  private _banner_id = 'integration_disconnected_banner'
  private _initialized = false
  private _disposeTitle: IReactionDisposer | null = null

  constructor(readonly entities: IEntitiesStore) {
    makeAutoObservable(this)
  }

  init = async () => {
    await Promise.all([
      this.entities.configStore.initPromise,
      this.entities.usersStore.initPromise,
      this.entities.integrationsStore.initPromise,
    ])

    runInAction(() => {
      this._initialized = true
    })
  }

  initReactions = () => {
    this.reactionTitle()
  }

  initBanner = () => {
    if (this.show) {
      bannerStore.add({
        id: this._banner_id,
        alert: {
          title: this._title,
          variant: EnumAlertBannerVariant.Warning,
          ariaLabel: 'integration_disconnected_banner',
        },
        action: this._isOwnerOrAdminRole
          ? {
              text: 'Reconnect',
              onAction: () => {
                this.onAction()
              },
            }
          : undefined,
        actionClose: {
          showCloseBtn: true,
        },
      })
    } else {
      bannerStore.remove(this._banner_id)
    }
  }

  reactionTitle = () => {
    this._disposeTitle?.()
    this._disposeTitle = reaction(
      () => this._title,
      (value) => {
        const banner = bannerStore.get(this._banner_id)

        if (banner) {
          banner.setAlertTitle(value)
        }
      }
    )
  }

  dispose = () => {
    this._disposeTitle?.()
    bannerStore.remove(this._banner_id)
  }

  onAction = () => {
    bannerStore.remove(this._banner_id)

    uiStore.changeRouteViaVue({
      path: `/${IntegrationsPath.root}`,
    })
  }

  get show() {
    return this._initialized && this._integrationDisconnected
  }

  private get _integrationsError() {
    return this.entities.integrationsStore.integrations.filter((item) => item.shouldReconnect)
  }

  private get _integrationsName() {
    return this._integrationsError.map((item) => item.name).join(', ')
  }

  private get _title() {
    if (this._isOwnerOrAdminRole) {
      return `Your ${this._integrationsName} integration disconnected.`
    }

    return `Your ${this._integrationsName} integration disconnected. Ask one of your admins to reconnect.`
  }

  private get _user() {
    return this.entities.usersStore.user
  }

  private get _integrationDisconnected() {
    return this.entities.configStore.integrationDisconnected
  }

  private get _isOwnerOrAdminRole() {
    if (!this._user) return false

    return this._user.isAdminOrOwnerRole
  }
}
